import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import ListService from "../../../Services/Shared/List.service";
/**
 * Turns elements into a forest.
 */
const treeify = (elements, parent = null) => {
    if (elements.length === 0) {
        return [];
    }
    return Array.from(ListService.groupBy(elements, (e) => e[0]).entries())
        .filter(([elm]) => elm != null)
        .map(([elm, rest]) => {
        const node = {
            elm,
            parent,
            span: rest.length,
        };
        node.children = treeify(rest.map((r) => r.slice(1)), node);
        return node;
    });
};
const grandparentsSquash = (node) => {
    if (node.parent == null) {
        return [];
    }
    return grandparentsSquash(node.parent).concat([node.parent.elm]);
};
/**
 * Groups the elements and provides which renderer index they should be called with and their rowspan.
 */
const getHierarchyElementAndSpan = (elements) => {
    const forest = treeify(elements);
    const results = [];
    let row = [];
    // Depth-first search through the tree using a stack.
    const stack = forest.map((t) => ({ tree: t, depth: 0 }));
    while (stack.length !== 0) {
        const elm = stack.shift();
        if (elm) {
            row.push({
                element: elm.tree.elm,
                index: elm.depth,
                span: elm.tree.span,
                parents: grandparentsSquash(elm.tree),
            });
            // Each leaf in the tree represents the end of a row.
            if (elm.tree.children.length === 0) {
                results.push(row);
                row = [];
            }
            stack.unshift(...elm.tree.children.map((c) => ({ tree: c, depth: elm.depth + 1 })));
        }
    }
    return results;
};
const DEFAULT_RENDERER = ({ val, provided, }) => {
    return _jsx("td", Object.assign({}, provided, { children: val }));
};
/**
 * A table body where one element might have sub elements which might have sub elements.  In other words,
 * a tabular representation of a tree of a series of trees.  Something like:
 * ```
 * | A | A1 | A11
 * |   |    | A12
 * |   | A2 | A21
 * |   |    | A22
 * ```
 * Where A1 and A2 are sub elements of A, and A11 and A12 are sub-elements of A1, etc.
 *
 * Needs to be put inside a `<table/>` element.  This only returns the `<tbody/>`.
 */
export const HierarchicalTableBody = ({ elements, renderers, flatten = false, hiddenColumn, }) => {
    const uniqued = React.useMemo(() => ListService.unique(elements, (e) => e.join("---")), [elements]);
    const rows = React.useMemo(() => {
        if (flatten) {
            return uniqued.map((row, i) => (_jsx("tr", { children: row.map((e, j) => {
                    var _a;
                    // Don't show the hidden column.
                    return hiddenColumn === j
                        ? null
                        : ((_a = renderers[j]) !== null && _a !== void 0 ? _a : DEFAULT_RENDERER)({
                            val: e,
                            provided: {},
                            otherOptions: row.slice(0, j).map((d) => [d]),
                        });
                }) }, i)));
        }
        /*
         * We need to "re-uniqueify" our rows because they may no longer unique when
         * we filter out a column.  Consider the case
         * [1, 1, 2]
         * [1, 2, 2]
         * they're unique. But, if we filter out column 1...
         * [1, 2]
         * [1, 2]
         */
        const uniquedAndFiltered = ListService.unique(uniqued.map((r) => r.filter((_, ci) => hiddenColumn !== ci)), (e) => e.join("---"));
        // We pass the columns into the hierarchyElement method without
        // the hidden column so that the hidden column is not considered.
        // This means that we have to add the hidden columns back in to
        // `otherOptions` manually (because they won't be included in the parents
        // because they're never passed in).  This is done below.
        return getHierarchyElementAndSpan(uniquedAndFiltered).map((row, i) => (_jsx("tr", { children: row.map(({ element, span, index, parents }) => {
                var _a;
                // We have to do something fancy because index is the index in the array
                // after we removed the hidden column (if there is one).  So, we
                // use this utility method to get the index of the renderer before the
                // hidden column was removed.  (For example, if we removed column 2 and the
                // index is 3, we actually need to use index 4).
                const originalIndex = ListService.getOriginalIndex(index, hiddenColumn == null ? [] : [hiddenColumn]);
                // Figuring out the other options
                const otherOptions = hiddenColumn != null && hiddenColumn <= index
                    ? ListService.insertIntoList(parents.map((d) => [d]), hiddenColumn, 
                    /*
                     * Find the unique set of elements at the hidden column index
                     * for the rows (elements) that match this one, up to the
                     * column that we're rendering, except for the hiddenColumn (which we splice out).
                     *
                     *  Why do we only consider columns up to the current one (originalIndex) that we're on?
                     *  Consider the example:
                     *  Ohio, Columbus, Grandview
                     *  Georgia, Columbus, Midland
                     *
                     *  If we hide the state column (index 0), then for the first row we're
                     *  given first the Columbus, Grandview row on the Columbus column.
                     *
                     *  Because we didn't include state, the parents of Columbus is [].
                     *  Our job below is "filling in" this empty space.  In order to do that,
                     *  we need to look through all the rows we're given and find
                     *  what Columbus is a "child of".  We don't really care what children
                     *  Columbus has.  We look through the rows and see that there are
                     *  two rows that have Columbus in the 1-index column (we ignore the 0-index column).
                     *  Therefore, we mark the parents of the Columbus column as [["Ohio", "Georgia"]].
                     *
                     *  Then, our next iteration finds "Grandview", also on the first column.
                     *  The parents are found to be [Columbus].  Our job is the same, we
                     *  have to fill in the first parent slot, the one that is "hidden".
                     *  To do this, we do the same thing. We look through the rows, ignoring
                     *  the hidden 0-index column, that has [Columbus] in the first column,
                     *  because it's our parent, and "Grandview" in the second.  We find one.
                     *  That row has "Ohio" in the hidden column, so the parents of this column
                     *  in this row become [["Ohio"], ["Columbus"]].  If there were additional
                     *  columns after this one, we don't care yet.  We'll consider them
                     *  when we get to them.
                     */
                    ListService.unique(elements
                        .filter((e) => ListService.shallowEqual(
                    // + 1 below because the second parameter in slice is not inclusive,
                    // and we want to include the element that we're currently on
                    e
                        .slice(0, originalIndex + 1)
                        .toSpliced(hiddenColumn, 1), 
                    // parents already won't include the hidden column, so we don't need to do anything
                    // besides adding the current element.
                    parents.concat([element]), []))
                        .map((e) => e[hiddenColumn])))
                    : parents.map((d) => [d]);
                return ((_a = renderers[originalIndex]) !== null && _a !== void 0 ? _a : DEFAULT_RENDERER)({
                    val: element,
                    otherOptions,
                    provided: { rowSpan: span },
                });
            }) }, i)));
    }, [elements, flatten, hiddenColumn, renderers, uniqued]);
    return _jsx("tbody", { children: rows });
};
